<template>
  <div class="row">
    <div class="col-md-12" style="margin: -15px; font-size: small;">
      <div class="card card-sm">
        <div class="card-header" style="background-color: #DBECDB;">
          <h5 class="card-title">Gestión de Compras</h5>
          <div class="row mb-3">
            <label class="col-md-1 text-end form-label">Fechas:</label>
            <div class="col-md-4">
              <div class="input-group">
                <input v-model="filters.fecha_inicio" type="date" class="form-control">
                <span class="input-group-text">a</span>
                <input v-model="filters.fecha_fin" type="date" class="form-control">
              </div>
            </div>
            <label class="col-md-1 text-end form-label">Categoría:</label>
            <div class="col-md-3">
              <select v-model="filters.categoria" class="form-select">
                <option value="">Todas</option>
                <option v-for="cat in categorias" :key="cat.id" :value="cat.id">
                  {{ cat.nombre_categoria }}
                </option>
              </select>
            </div>
            <div class="col-md-8">
              <div class="form-group">
                <div class="input-group">
                  <input
                    v-model="filters.search"
                    type="text"
                    class="form-control"
                    placeholder="Buscar por preventivo, partida o proveedor..."
                  >
                  <button
                    class="btn btn-outline-secondary"
                    type="button"
                    @click="handleSearch"
                  >
                    <i class="fa fa-search"></i>
                  </button>
                </div>
              </div>
            </div>

            <div class="col-md-4 text-end">
              <BButton
                @click="showModal(null)"
                v-if="hasPermission('compra_create')"
                variant="primary"
                class="btn-round me-2"
              >
                <i class="fa fa-plus me-1"></i> Nueva Compra
              </BButton>
              <BButton
                variant="danger"
                class="btn-round"
                @click="generatePDF"
              >
                <i class="fa fa-file-pdf me-1"></i> PDF
              </BButton>
            </div>
          </div>
        </div>

        <div class="card-body">
          <div v-if="loading" class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Cargando...</span>
            </div>
          </div>

          <div v-else>
            <div class="table-responsive">
              <table class="table table-sm table-striped table-hover table-bordered">
                <thead class="text-primary">
                  <tr style="line-height: 8px; font-size: 0.8rem;">
                    <th>Nro.</th>
                    <th>Fecha</th>
                    <th>Preventivo</th>
                    <th>Partida</th>
                    <th>Categoría</th>
                    <th>Proveedor</th>
                    <th>Descripcion</th>
                    <th>Items</th>
                    <th>Acta</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="compra in compras.data" :key="compra.id">
                    <td>{{ compra.numero }}</td>
                    <td>{{ formatDate(compra.fecha_compra) }}</td>
                    <td>{{ compra.nro_preventivo }}</td>
                    <td>{{ compra.categoria.id_partida }}</td>
                    <td>{{ compra.categoria.nombre_categoria }}</td>
                    <td>{{ compra.proveedores.razon_social }}</td>
                    <td>{{ compra.acta_entrega }}</td>
                    <td>{{ compra.detalles_count || 0 }}</td>
                    <td>
                      <BButton
                          v-if="compra.estado == 'Finalizado'"
                          variant="success"
                          class="btn btn-sm"
                          @click="generateActaPDF(compra.id)"
                        >
                          <i class="fa fa-file-pdf me-1"></i>
                      </BButton>
                    </td>

                    <td>
                      <div class="btn-group">
                        <BButton
                          @click="goToDetails(compra.id)"
                          variant="info"
                          size="sm"
                          class="me-1"
                        >
                          <i class="fa fa-eye"></i>
                        </BButton>
                        <BButton
                          @click="showModal(compra)"
                          v-if="compra.estado == 'Iniciado' && hasPermission('compra_update')"
                          variant="warning"
                          size="sm"
                          class="me-1"
                        >
                          <i class="fa fa-edit"></i>
                        </BButton>
                        <BButton
                          @click="confirmDelete(compra)"
                          v-if="compra.estado == 'Iniciado' && hasPermission('compra_delete')"
                          variant="danger"
                          size="sm"
                        >
                          <i class="fa fa-trash"></i>
                        </BButton>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Paginación -->
            <div class="row mt-3" v-if="compras.total > 0">
              <div class="col-md-5 text-center text-muted mt-2">
                Mostrando {{ ((pagination.current_page - 1) * pagination.per_page) + 1 }}
                a {{ Math.min(pagination.current_page * pagination.per_page, compras.total) }}
                de {{ compras.total }} registros
              </div>
              <div class="col-md-7 d-flex justify-content-center">
                <BPagination
                  v-model="pagination.current_page"
                  :total-rows="compras.total"
                  :per-page="pagination.per_page"
                  @update:modelValue="handlePageChange"
                  align="center"
                  size="md"
                  class="mt-3"
                  :limit="10"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para crear/editar compra -->
    <BModal
      v-model="modalVisible"
      :title="modalTitle"
      size="lg"
      centered
      hide-footer
      no-close-on-backdrop
    >
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-4">
            <div class="form-group">
              <label class="form-label">Fecha Compra</label>
              <input
                type="date"
                v-model="form.fecha_compra"
                class="form-control"
                :class="{ 'is-invalid': errors.fecha_compra }"
                required
              />
              <div class="invalid-feedback" v-if="errors.fecha_compra">
                {{ errors.fecha_compra[0] }}
              </div>
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group">
              <label class="form-label">N° Preventivo</label>
              <input
                type="text"
                v-model="form.nro_preventivo"
                class="form-control"
                :class="{ 'is-invalid': errors.nro_preventivo }"
                required
              />
              <div class="invalid-feedback" v-if="errors.nro_preventivo">
                {{ errors.nro_preventivo[0] }}
              </div>
            </div>
          </div>
        </div>
        <!--
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <label class="form-label">Partida Presupuestaria</label>
              <select
                v-model="form.partida_presupuestaria"
                class="form-select"
                :class="{ 'is-invalid': errors.partida_presupuestaria }"
                required
              >
                <option value="">Seleccione una partida</option>
                <option
                  v-for="partida in partidas"
                  :key="partida.codigo"
                  :value="partida.codigo"
                >
                  {{ partida.codigo }}:{{ partida.nombre }}
                </option>
              </select>

              <div class="invalid-feedback" v-if="errors.partida_presupuestaria">
                {{ errors.partida_presupuestaria[0] }}
              </div>
            </div>
          </div>
        </div>
        -->
        <div class="row mt-3">
          <div class="col-md-6">
            <div class="form-group">
              <label class="form-label">Proveedor</label>
              <select
                v-model="form.proveedor"
                class="form-select"
                :class="{ 'is-invalid': errors.proveedor }"
                required
              >
                <option value="">Seleccione una proveedor</option>
                <option
                  v-for="proveedor in proveedores"
                  :key="proveedor.id"
                  :value="proveedor.id"
                >
                  {{ proveedor.razon_social }}
                </option>
              </select>

              <div class="invalid-feedback" v-if="errors.proveedor">
                {{ errors.proveedor[0] }}
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group">
              <label class="form-label">Categoría</label>
              <select
                v-model="form.id_categoria"
                class="form-select"
                :class="{ 'is-invalid': errors.id_categoria }"
                required
              >
                <option value="">Seleccione una categoría</option>
                <option
                  v-for="categoria in categorias"
                  :key="categoria.id"
                  :value="categoria.id"
                >
                  {{ categoria.id_partida }}: {{ categoria.nombre_categoria }}
                </option>
              </select>
              <div class="invalid-feedback" v-if="errors.id_categoria">
                {{ errors.id_categoria[0] }}
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-md-12">
            <div class="form-group">
              <label class="form-label">Descripcion</label>
              <input
                type="text"
                v-model="form.acta_entrega"
                class="form-control"
                :class="{ 'is-invalid': errors.acta_entrega }"
              />
              <div class="invalid-feedback" v-if="errors.acta_entrega">
                {{ errors.acta_entrega[0] }}
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer mt-3">
          <BButton
            type="button"
            variant="secondary"
            @click="modalVisible = false"
            class="me-2"
          >
            Cancelar
          </BButton>
          <BButton
            type="submit"
            variant="primary"
            :disabled="loadingForm"
          >
            <span v-if="loadingForm" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            {{ isEditing ? 'Actualizar' : 'Guardar' }}
          </BButton>
        </div>
      </form>
    </BModal>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import Swal from 'sweetalert2';
import { BModal, BButton, BPagination } from 'bootstrap-vue-3';

const router = useRouter();

// Store
const authStore = useAuthStore();

// Datos
const compras = ref({ data: [] });
const categorias = ref([]);
const proveedores = ref([]);
const partidas = ref([]);
const loading = ref(true);
const loadingForm = ref(false);
const modalVisible = ref(false);
const isEditing = ref(false);
const errors = ref({});

// Filtros y paginación
const filters = ref({
  search: '',
  fecha_inicio: '',
  fecha_fin: '',
  categoria: ''
});

const pagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0
});

// Formulario
const form = ref({
  id: null,
  fecha_compra: new Date().toISOString().split('T')[0],
  nro_preventivo: '',
  proveedor: '',
  id_categoria: '',
  acta_entrega: ''
});

// Computed
const modalTitle = computed(() => {
  return isEditing.value ? 'Editar Compra' : 'Nueva Compra';
});

// Métodos
const fetchCompras = async () => {
  loading.value = true;
  try {
    const params = {
      page: pagination.value.current_page,
      per_page: pagination.value.per_page,
      search: filters.value.search,
      fecha_inicio: filters.value.fecha_inicio || undefined,
      fecha_fin: filters.value.fecha_fin || undefined,
      categoria: filters.value.categoria || undefined
    };

    // Limpiar parámetros vacíos
    Object.keys(params).forEach(key => {
      if (params[key] === '' || params[key] === undefined) {
        delete params[key];
      }
    });

    const response = await axios.get('/compras/list', { params });

    // Verificar la estructura de la respuesta
    if (response.data.data) {
      compras.value = response.data.data;
      pagination.value.total = response.data.data.total;
      pagination.value.current_page = response.data.data.current_page;
      pagination.value.per_page = response.data.data.per_page;
    } else if (Array.isArray(response.data)) {
      // Si la respuesta es un array simple
      compras.value = {
        data: response.data,
        total: response.data.length,
        current_page: 1,
        last_page: 1,
        per_page: response.data.length
      };
      pagination.value.total = response.data.length;
    } else {
      // Si la respuesta tiene otra estructura
      compras.value = response.data;
      pagination.value.total = response.data.total || response.data.length || 0;
    }
  } catch (error) {
    console.error('Error al cargar compras:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'No se pudieron cargar las compras'
    });
  } finally {
    loading.value = false;
  }
};

const fetchCategorias = async () => {
  try {
    const response = await axios.get('/compras/categorias');
    categorias.value = response.data.data;
  } catch (error) {
    console.error('Error al cargar categorías:', error);
  }
};

const fetchPartidas = async () => {
  try {
    const response = await axios.get('/compras/partidas');
    partidas.value = response.data.data;
  } catch (error) {
    console.error('Error al cargar partidas:', error);
  }
};

const fetchProveedores = async () => {
  try {
    const response = await axios.get('/proveedor/select');
    proveedores.value = response.data.data;
  } catch (error) {
    console.error('Error al cargar proveedores:', error);
  }
};

const showModal = (compra) => {
  errors.value = {};
  if (compra) {
    // Edición
    isEditing.value = true;
    form.value = {
      id: compra.id,
      fecha_compra: compra.fecha_compra,
      nro_preventivo: compra.nro_preventivo,
      //partida_presupuestaria: compra.partida_presupuestaria,
      proveedor: compra.proveedor,
      id_categoria: compra.id_categoria,
      acta_entrega: compra.acta_entrega
    };
  } else {
    // Nuevo
    isEditing.value = false;
    form.value = {
      id: null,
      fecha_compra: new Date().toISOString().split('T')[0],
      nro_preventivo: '',
      //partida_presupuestaria: '',
      proveedor: '',
      id_categoria: '',
      acta_entrega: ''
    };
  }
  modalVisible.value = true;
};

const goToDetails = (compraId) => {
  router.push({ name: 'compras-detalle', params: { id: compraId } });
};

const handleSubmit = async () => {
  loadingForm.value = true;
  errors.value = {};

  try {
    let response;

    if (isEditing.value && form.value.id) {
      response = await axios.post(`/compras/${form.value.id}/update`, form.value);
    } else {
      response = await axios.post('/compras/save', form.value);
    }

    Swal.fire({
      icon: 'success',
      title: 'Éxito',
      text: response.data.message
    });

    modalVisible.value = false;
    fetchCompras();
  } catch (error) {
    if (error.response && error.response.status === 422) {
      errors.value = error.response.data.errors;
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Ocurrió un error al procesar la solicitud'
      });
    }
  } finally {
    loadingForm.value = false;
  }
};

const confirmDelete = (compra) => {
  Swal.fire({
    title: '¿Está seguro?',
    text: `¿Desea eliminar la compra con preventivo ${compra.nro_preventivo}?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  }).then((result) => {
    if (result.isConfirmed) {
      deleteCompra(compra.id);
    }
  });
};

const deleteCompra = async (id) => {
  try {
    await axios.get(`/compras/${id}/delete`);
    Swal.fire({
      icon: 'success',
      title: 'Compra eliminada',
      text: 'La compra ha sido eliminada correctamente'
    });
    await fetchCompras();
  } catch (error) {
    console.error('Error al eliminar compra:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.response?.data?.message || 'No se pudo eliminar la compra'
    });
  }
};

const handleSearch = () => {
  pagination.value.current_page = 1;
  fetchCompras();
};

const handlePageChange = (page) => {
  pagination.value.current_page = page;
  fetchCompras();
};

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
  return new Date(dateString).toLocaleDateString('es-BO', options);
};

const generateActaPDF = async (id) => {
  try {
            const response = await axios.get(`/compras/acta/${id}`, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = 'acta-compra.pdf';
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error('Error al generar PDF:', error);
        }
};

const generatePDF = async () => {
  try {
    const params = {
      search: filters.value.search,
      fecha_inicio: filters.value.fecha_inicio,
      fecha_fin: filters.value.fecha_fin,
      categoria: filters.value.categoria,
    };

    const response = await axios.get('/compras/reporte/generar', {
      params,
      responseType: 'blob'
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `reporte-compras-${new Date().toISOString().split('T')[0]}.pdf`);
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    console.error('Error al generar PDF:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'No se pudo generar el PDF'
    });
  }
};

const hasPermission = (permission) => {
  return authStore.hasPermission(permission);
};

// Ciclo de vida
onMounted(() => {
  fetchCompras();
  fetchCategorias();
  fetchPartidas();
  fetchProveedores();
});
</script>

<style scoped>
  thead th {
    background-color: #D84315;
    color:#ffffff;
    position: sticky;
    top: 0;
  }
  tbody td {
    margin: 0px;
    padding: 8px !important;
    vertical-align: middle;
  }
  .table-responsive {
    max-height: 600px;
    overflow-y: auto;
  }
  .btn-round {
    border-radius: 50px;
  }
</style>
